import GUI, {type IGUIProps} from "../GUI"
import React, { ForwardedRef, useRef, useState, MouseEvent, useEffect, useImperativeHandle, forwardRef } from "react"
import Select, { TPrimitiveId } from "../Select/Select"
import { usePath } from "../../libs/path/PathProvider"
import MenuButton from "../Menu/MenuButton"
import MenuIcon from "../Icons/MenuIcon"
import { UpdateEvent } from "../GUIEvents"
import { TMenuProps, TMenuRow, useMenu } from "../Menu/Menu"
import { TAnchorProps } from "../Overlay/AnchoredBackdrop"

export {type TMenuRow }
type TTabMenuProps<ID extends TPrimitiveId> = {
    disabled?:boolean
    options:TMenuRow<ID>[]
    menuIcon?:JSX.Element
    anchorProps?:TAnchorProps
    menuProps?:Omit<TMenuProps<ID>, "options">
    titleRenderer?:(option:TMenuRow<ID>) => JSX.Element
    onUpdate?:(e:UpdateEvent<ID, HTMLDivElement>) => void
}
export type TTabsProps<ID extends TPrimitiveId> = IGUIProps & TTabMenuProps<ID> & {
    value?:ID
    defaultValue?:ID
    breakpoint?:number
    disabled?:boolean
    onTab?:(id:ID) => void
    anchorProps?:TAnchorProps
    tabProps?:(option:TMenuRow<ID>) => Record<string,any>
    tabRenderer?:(option:TMenuRow<ID>) => React.ReactNode
    preIcon?:React.ReactNode
}

export interface ITabs<ID extends TPrimitiveId> {
    value:ID|null
}

function shouldBreak(tabs:HTMLDivElement|null, breakpoint:number|undefined){
    if (breakpoint===undefined) return false
    else if (breakpoint<0) {
        const scroller = tabs?.querySelector(".scroller")?.getBoundingClientRect()
        const self = tabs?.getBoundingClientRect()
        return !!scroller && !!self && scroller.width>self.width
    }
    else return window.innerWidth<breakpoint
}

function TabMenu<ID extends TPrimitiveId>(props:TTabMenuProps<ID>&{value:ID|null}) {
    const menuProps : TMenuProps<ID> = {options:props.options, ...props.menuProps}
    const defaultValue = props.value===null ? undefined : props.value
    return <div className="menuTitle flex middle">
        <MenuButton className="icon" anchorProps={props.anchorProps} menuProps={menuProps} onUpdate={props.onUpdate} defaultValue={defaultValue} disabled={props.disabled}>{props.menuIcon || <MenuIcon/>}</MenuButton>
    </div>
}

function Tabs<ID extends TPrimitiveId>(props:TTabsProps<ID>, ref?:ForwardedRef<ITabs<ID>>) {
    const [compact,setCompact] = useState<boolean>(false)
    const [value,setValue] = useState<ID|null>(props.defaultValue !==undefined ? props.defaultValue : props.value === undefined ? null : props.value)
    const [busy,setBusy] = useState<boolean>(false)
    const refSelf = useRef<HTMLDivElement>(null)
    const mounted = useRef<boolean>(false)
    const {selectMenu} = useMenu<ID>()

    const _onTab = (tab:TMenuRow<ID>) => {
        const id = Select.id(tab)
        props.onTab?.(id)
        setValue(id)
        selectMenu(tab)
    }
    const _setValue = async (id:ID) => {
        if (!refSelf.current || busy) return

        const from = value!==null ? Select.indexOf(props.options, value) : -1
        const to = Select.indexOf(props.options, id)
        if (to<0 || from==to) return

        _onTab(Select.option(props.options, id))

        const tabFrom = refSelf.current.querySelector(`.tab:nth-child(${from+1})`)
        const tabTo = refSelf.current.querySelector(`.tab:nth-child(${to+1})`)
        if (tabFrom && tabTo) {
            setBusy(true)
            const rFrom = tabFrom.getBoundingClientRect(), rTo = tabTo.getBoundingClientRect()
            const border = tabTo.querySelector(".border") as HTMLDivElement;
            border.style.setProperty("width", tabFrom.clientWidth + "px")
            border.style.setProperty("left", (rFrom.left - rTo.left) + "px")
            await GUI.tick()
            if (!refSelf.current) return
            border.classList.add("animate")
            await GUI.tick()
            if (!refSelf.current) return
            border.style.setProperty("width", "100%")
            border.style.setProperty("left", "0")
            await GUI.transitionEnd(border)
            if (!refSelf.current) return
            border.classList.remove("animate")
        }
        setBusy(false)
    }

    const onClick = (option:TMenuRow<ID>) => (e:MouseEvent<HTMLDivElement>) => _setValue(Select.id(option))
    const onUpdate = (e:UpdateEvent<ID,HTMLDivElement>) => e.value!==undefined && e.value!==null && _onTab(Select.option(props.options, e.value))
    const onResize = () => setCompact(shouldBreak(refSelf.current, props.breakpoint))
    useEffect(() => {
        if (mounted.current) props.value !== undefined && _setValue(props.value)
        else mounted.current = true
    }, [props.value])

    useEffect(() => {
        window.addEventListener("resize", onResize)
        onResize()
        return () => { window.removeEventListener("resize", onResize)}
    }, [])
    useImperativeHandle(ref, () => ({
        get value() { return value}
    }))

    const tabs = props.options.map(option => {
        const {className,...tabProps} = props.tabProps?.(option) || {}
        const tab = props.tabRenderer?.(option) || Select.text(option)
        const id = Select.id(option)
        return <div className={GUI.joinClass("tab", className, value==id ? "selected" : "")} {...tabProps} key={id} onClick={onClick(option)}>{tab}<div className="border"/></div>
    })

    const title = compact ? (value ? props.titleRenderer?.(value) ?? Select.text(Select.option(props.options, value)) : null) : null
    const menuButton = compact 
        ? <><div className="flex1 title">{title}</div><TabMenu options={props.options} anchorProps={props.anchorProps} menuIcon={props.menuIcon} value={value} onUpdate={onUpdate} disabled={props.disabled}/></>
        : null
    return <div className={GUI.joinClass(props.className, "tabs", props.disabled ? "disabled" : "", compact ? "compact" : "")} ref={refSelf}>
        {props.preIcon ? props.preIcon : <></>}
        <div className="scroller">{tabs}</div>
        {compact ? menuButton : <></>}
    </div>
}

export default forwardRef(Tabs) as <ID extends TPrimitiveId>(props:TTabsProps<ID> & {ref?:ForwardedRef<ITabs<ID>>}) => JSX.Element